import { getBackendUrl } from '../utils/backendUrl'

export const CSRF_HEADER = 'X-CSRF-TOKEN'

const CSRF_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}\.[A-Za-z0-9_-]{43}$/

let csrfToken = null
let authGeneration = 0
let refreshBlocked = false
let refreshOperation = null
let reauthenticationHandler = null
let authorizationChangeHandler = null
let reauthenticationPending = false

export class BrowserAuthError extends Error {
  constructor(message, { code = 'browser_auth_failed', status = null } = {}) {
    super(message)
    this.name = 'BrowserAuthError'
    this.code = code
    this.status = status
  }
}

export class MissingCsrfTokenError extends BrowserAuthError {
  constructor() {
    super('Browser authentication requires sign-in.', {
      code: 'csrf_token_missing',
    })
    this.name = 'MissingCsrfTokenError'
  }
}

function readHeader(headers, name) {
  if (!headers) return null

  if (typeof headers.get === 'function') return headers.get(name)

  const key = Object.keys(headers).find(
    (header) => header.toLowerCase() === name.toLowerCase(),
  )

  return key ? headers[key] : null
}

function validCsrfToken(value) {
  return typeof value === 'string' && CSRF_TOKEN_PATTERN.test(value)
}

function abortRefreshOperation() {
  const operation = refreshOperation
  operation?.controller.abort()
  return operation?.promise ?? null
}

function advanceAuthGeneration({ preserveToken = false, blockRefresh = true } = {}) {
  authGeneration += 1
  refreshBlocked = blockRefresh
  if (!preserveToken) csrfToken = null
  return abortRefreshOperation()
}

export function captureCsrfToken(
  body,
  headers,
  { generation = authGeneration } = {},
) {
  const bodyToken = body?.csrf_token
  const headerToken = readHeader(headers, CSRF_HEADER)

  if (generation !== authGeneration || refreshBlocked) {
    throw new BrowserAuthError('Stale browser refresh was discarded.', {
      code: 'stale_refresh',
    })
  }

  if (
    bodyToken !== undefined &&
    headerToken !== null &&
    bodyToken !== headerToken
  ) {
    clearCsrfToken()
    throw new BrowserAuthError('Browser authentication response was invalid.', {
      code: 'csrf_token_mismatch',
    })
  }

  const nextToken = bodyToken ?? headerToken

  if (!validCsrfToken(nextToken)) {
    clearCsrfToken()
    throw new BrowserAuthError('Browser authentication response was invalid.', {
      code: 'csrf_token_invalid',
    })
  }

  csrfToken = nextToken
  reauthenticationPending = false

  return nextToken
}

export function getCsrfToken() {
  return csrfToken
}

export function hasCsrfToken() {
  return csrfToken !== null
}

export function getAuthGeneration() {
  return authGeneration
}

export function hasActiveBrowserSession() {
  return csrfToken !== null && refreshBlocked === false
}

export function clearCsrfToken() {
  csrfToken = null
}

export function beginBrowserAuthentication() {
  advanceAuthGeneration({ blockRefresh: false })
  reauthenticationPending = false
}

export function acceptAuthenticatedUserSession() {
  advanceAuthGeneration({ preserveToken: true, blockRefresh: false })
  reauthenticationPending = false
}

export function invalidateBrowserSession() {
  return advanceAuthGeneration()
}

export function setReauthenticationHandler(handler) {
  reauthenticationHandler = typeof handler === 'function' ? handler : null
}

export function setAuthorizationChangeHandler(handler) {
  authorizationChangeHandler = typeof handler === 'function' ? handler : null
}

export function notifyAuthorizationChange(reason = 'authorization_denied') {
  authorizationChangeHandler?.(reason)
}

export function requireReauthentication(reason = 'session_changed') {
  if (reauthenticationPending) {
    clearCsrfToken()
    return
  }

  reauthenticationPending = true
  invalidateBrowserSession()
  reauthenticationHandler?.(reason)
}

function authUrl(action) {
  const path = `/api/auth/${action}`

  if (import.meta.env.DEV) return path

  return `${getBackendUrl()}${path}`
}

async function responseData(response) {
  try {
    return await response.json()
  } catch {
    return null
  }
}

function responseError(response, data, fallback) {
  return new BrowserAuthError(fallback, {
    code:
      typeof data?.error === 'string' ? data.error : 'browser_auth_failed',
    status: response.status,
  })
}

async function postBrowserAuth(
  action,
  { requestToken = csrfToken, signal } = {},
) {
  if (!requestToken) throw new MissingCsrfTokenError()

  const response = await fetch(authUrl(action), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      [CSRF_HEADER]: requestToken,
    },
    signal,
  })
  const data = await responseData(response)

  if (!response.ok || data?.success !== true) {
    throw responseError(
      response,
      data,
      `${action === 'logout' ? 'Logout' : 'Session refresh'} failed.`,
    )
  }

  return { data, headers: response.headers }
}

async function rotateBrowserSession(generation, requestToken, controller) {
  try {
    const response = await postBrowserAuth('refresh', {
      requestToken,
      signal: controller.signal,
    })

    if (generation !== authGeneration || refreshBlocked) {
      throw new BrowserAuthError('Stale browser refresh was discarded.', {
        code: 'stale_refresh',
      })
    }

    captureCsrfToken(response.data, response.headers, { generation })
    return response.data
  } catch (error) {
    if (generation === authGeneration) clearCsrfToken()

    if (generation !== authGeneration || controller.signal.aborted) {
      throw new BrowserAuthError('Stale browser refresh was discarded.', {
        code: 'stale_refresh',
      })
    }

    throw error
  }
}

export function refreshBrowserSession() {
  if (refreshBlocked) {
    return Promise.reject(
      new BrowserAuthError('Browser refresh is blocked.', {
        code: 'refresh_blocked',
      }),
    )
  }

  if (!csrfToken) return Promise.reject(new MissingCsrfTokenError())

  if (refreshOperation?.generation === authGeneration) {
    return refreshOperation.promise
  }

  refreshOperation?.controller.abort()

  const generation = authGeneration
  const requestToken = csrfToken
  const controller = new AbortController()
  const promise = rotateBrowserSession(generation, requestToken, controller)
  const operation = { controller, generation, promise }
  refreshOperation = operation
  promise.then(
    () => {
      if (refreshOperation === operation) refreshOperation = null
    },
    () => {
      if (refreshOperation === operation) refreshOperation = null
    },
  )

  return promise
}

export async function logoutBrowserSession() {
  const requestToken = csrfToken
  const pendingRefresh = advanceAuthGeneration()

  if (pendingRefresh) {
    await pendingRefresh.catch(() => undefined)
  }

  try {
    const response = await postBrowserAuth('logout', { requestToken })
    return response.data
  } finally {
    clearCsrfToken()
  }
}
